import type { GameState } from '@/types'
import { GameEngine } from '@/game/GameEngine'

export type CellClickCallback = (num: number) => void

type CellState = 'idle' | 'active' | 'called' | 'out-of-range' | 'bomb'

/**
 * Renders and patches the 10×10 number grid.
 * Supports both initial mount and efficient partial updates.
 */
export class NumberGrid {
  private container: HTMLElement
  private cells: HTMLElement[] = []
  private onClick: CellClickCallback

  constructor(container: HTMLElement, onClick: CellClickCallback) {
    this.container = container
    this.onClick = onClick
  }

  /** Initial render — creates all 100 cells */
  mount(gameState: GameState): void {
    this.container.innerHTML = ''
    this.cells = []

    for (let n = 1; n <= 100; n++) {
      const cell = document.createElement('div')
      cell.className = 'num-cell'
      cell.dataset.num = String(n)
      cell.textContent = String(n)
      cell.addEventListener('click', () => {
        if (GameEngine.isCallable(gameState, n)) {
          this.onClick(n)
        }
      })
      this.container.appendChild(cell)
      this.cells.push(cell)
    }

    this.patch(gameState)
  }

  /** Efficient patch — only updates changed cells */
  patch(state: GameState): void {
    for (let i = 0; i < this.cells.length; i++) {
      const num = i + 1
      const cell = this.cells[i]
      const desired = this.getCellState(state, num)
      const current = cell.dataset.state as CellState | undefined

      if (current !== desired) {
        cell.dataset.state = desired
        cell.className = `num-cell num-cell--${desired}`
      }
    }
  }

  private getCellState(state: GameState, num: number): CellState {
    if (!state.isRunning && !state.isOver) return 'idle'
    if (state.isOver && num === state.loserNumber) return 'bomb'
    if (num < state.rangeMin || num > state.rangeMax) return 'out-of-range'
    if (state.calledNumbers.has(num)) return 'called'
    return 'active'
  }
}
